// src/components/rodeoAi/markdownComponents.js
import React from 'react';
import {
    Typography,
    Link,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper
} from '@mui/material';

export const markdownComponents = {
    h1: ({ node, ...props }) => (
        <Typography
            variant="h4"
            gutterBottom
            sx={{
                fontWeight: 700,
                mt: 2,
                fontSize: { xs: '1.5rem', sm: '2rem' }
            }}
            {...props}
        />
    ),
    h2: ({ node, ...props }) => (
        <Typography
            variant="h5"
            gutterBottom
            sx={{
                fontWeight: 600,
                mt: 2,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
            {...props}
        />
    ),
    h3: ({ node, ...props }) => (
        <Typography
            variant="h6"
            gutterBottom
            sx={{
                fontWeight: 600,
                mt: 1.5,
                fontSize: { xs: '1.125rem', sm: '1.25rem' }
            }}
            {...props}
        />
    ),
    p: ({ node, ...props }) => (
        <Typography
            variant="body1"
            paragraph
            sx={{
                mb: 1.5,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                lineHeight: 1.6
            }}
            {...props}
        />
    ),

    a: ({ node, ...props }) => (
        <Link
            {...props}
            sx={{
                color: '#007FFF',
                textDecoration: 'underline',
                fontWeight: 500,
                transition: 'color 0.2s ease-in-out',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                wordBreak: 'break-word',
                '&:hover': {
                    color: '#66B2FF',
                    textDecoration: 'underline'
                }
            }}
        />
    ),

    ul: ({ node, ...props }) => (
        <Box
            component="ul"
            sx={{
                pl: { xs: 2.5, sm: 3 },
                mb: 2
            }}
            {...props}
        />
    ),

    ol: ({ node, ...props }) => (
        <Box
            component="ol"
            sx={{
                pl: { xs: 2.5, sm: 3 },
                mb: 2
            }}
            {...props}
        />
    ),

    li: ({ node, ...props }) => (
        <Box component="li" sx={{ mb: 0.5 }}>
            <Typography
                component="span"
                variant="body1"
                sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: 1.6
                }}
                {...props}
            />
        </Box>
    ),

    // Table components - Mobile optimized
    table: ({ node, ...props }) => (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={(theme) => ({
                mb: 2,
                mt: 1,
                borderColor:
                    theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.1)',
                backgroundColor:
                    theme.palette.mode === 'dark'
                        ? 'rgba(19, 47, 76, 0.4)'
                        : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                // Enable horizontal scroll on mobile
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                // Add scrollbar styling
                '&::-webkit-scrollbar': {
                    height: { xs: '6px', sm: '8px' }
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.2)'
                            : 'rgba(0,0,0,0.2)',
                    borderRadius: '3px'
                }
            })}
        >
            <Table
                size="small"
                sx={{
                    minWidth: { xs: 300, sm: 400 }  // Ensure table has minimum width
                }}
                {...props}
            />
        </TableContainer>
    ),

    thead: ({ node, ...props }) => (
        <TableHead
            sx={(theme) => ({
                backgroundColor:
                    theme.palette.mode === 'dark'
                        ? 'rgba(0, 127, 255, 0.1)'
                        : 'rgba(0, 127, 255, 0.05)',
                // Sticky header on mobile for better scrolling
                position: 'sticky',
                top: 0,
                zIndex: 1
            })}
            {...props}
        />
    ),

    tbody: ({ node, ...props }) => <TableBody {...props} />,

    tr: ({ node, ...props }) => (
        <TableRow
            sx={(theme) => ({
                '&:nth-of-type(odd)': {
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.02)'
                            : 'rgba(0,0,0,0.02)'
                },
                '&:hover': {
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.05)'
                }
            })}
            {...props}
        />
    ),

    td: ({ node, ...props }) => (
        <TableCell
            sx={(theme) => ({
                color: theme.palette.text.primary,
                borderColor:
                    theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.1)',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px', sm: '12px' },
                whiteSpace: { xs: 'nowrap', sm: 'normal' }
            })}
            {...props}
        />
    ),

    th: ({ node, ...props }) => (
        <TableCell
            component="th"
            scope="col"
            sx={(theme) => ({
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                borderColor:
                    theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.1)',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px', sm: '12px' },
                whiteSpace: { xs: 'nowrap', sm: 'normal' }
            })}
            {...props}
        />
    ),

    // Code blocks
    code({ node, inline, className, children, ...props }) {
        return !inline ? (
            <Box
                sx={(theme) => ({
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(0, 0, 0, 0.3)'
                            : theme.palette.grey[100],
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 1,
                    my: 2,
                    overflowX: 'auto',
                    fontFamily: 'monospace',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    border:
                        theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.1)'
                            : '1px solid rgba(0,0,0,0.1)',
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': {
                        height: { xs: '6px', sm: '8px' }
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor:
                            theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.2)'
                                : 'rgba(0,0,0,0.2)',
                        borderRadius: '3px'
                    }
                })}
            >
                <pre style={{ margin: 0 }}>
                    <code {...props}>{children}</code>
                </pre>
            </Box>
        ) : (
            <code
                style={{
                    backgroundColor: 'rgba(0,127,255,0.1)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.875em'
                }}
                {...props}
            >
                {children}
            </code>
        );
    },

    // Horizontal rule
    hr: ({ node, ...props }) => (
        <Box
            component="hr"
            sx={(theme) => ({
                border: 'none',
                borderTop:
                    theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.1)',
                my: 2
            })}
            {...props}
        />
    ),

    // Blockquote
    blockquote: ({ node, ...props }) => (
        <Box
            component="blockquote"
            sx={(theme) => ({
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                pl: { xs: 1.5, sm: 2 },
                py: 0.5,
                my: 2,
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                backgroundColor:
                    theme.palette.mode === 'dark'
                        ? 'rgba(0, 127, 255, 0.05)'
                        : 'rgba(0, 127, 255, 0.03)'
            })}
            {...props}
        />
    )
};